import { useTranslation } from 'react-i18next';
import { Sparkles, Undo2 } from 'lucide-react';
import { Card } from './Card';
import { useToast } from './Toast';
import { useAiActions, useUndoAiAction, type AiAction } from '../../hooks/useAiActions';
import { useCan } from '../../hooks/useCan';
import { relativeTime } from '../../lib/format';

/**
 * Wave 7 — reusable "AI activity" receipts card.
 *
 * Lists recent ai_actions (the AI agent's named accepted actions) with agent
 * name, summary, relative time, and an inline Undo where allowed. Undone
 * actions render struck-through. Undo is viewer-gated (canMutate) and only
 * offered for kinds that are reversible while the row isn't already undone.
 */
const UNDOABLE_KINDS = new Set(['apply_account_suggestion']);

export function AiActivityCard({
  wsId,
  limit = 8,
  className,
}: {
  wsId: string | undefined;
  limit?: number;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const { canMutate } = useCan();
  const toast = useToast();
  const { data, isLoading } = useAiActions(wsId, limit);
  const undo = useUndoAiAction(wsId);

  const onUndo = (a: AiAction) =>
    undo.mutate(a.id, {
      onError: (err: any) =>
        toast.error(
          err?.response?.data?.message ?? t('aiActions.cannotUndo'),
        ),
    });

  return (
    <section className={className}>
      <h2 className="font-medium mb-3 flex items-center gap-2">
        <Sparkles size={16} className="text-accent-600" />
        {t('aiActions.activityTitle')}
      </h2>
      {isLoading ? (
        <Card className="p-8 text-center text-ink-500 text-sm">{t('common.loading')}</Card>
      ) : !data || data.length === 0 ? (
        <Card className="p-8 text-center text-ink-500 text-sm">{t('aiActions.empty')}</Card>
      ) : (
        <ul className="space-y-2">
          {data.map((a) => {
            const undone = !!a.undone_at;
            const canUndo = canMutate && !undone && UNDOABLE_KINDS.has(a.kind);
            return (
              <li key={a.id}>
                <Card className="p-3.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs text-ink-500 mb-0.5">
                      <span className="font-medium text-accent-700 dark:text-accent-300">
                        {a.agent}
                      </span>
                      <span aria-hidden>·</span>
                      <span>{relativeTime(a.created_at, i18n.language)}</span>
                      {undone && (
                        <>
                          <span aria-hidden>·</span>
                          <span className="text-ink-400">{t('aiActions.undone')}</span>
                        </>
                      )}
                    </div>
                    <div
                      className={
                        undone
                          ? 'text-sm text-ink-400 line-through'
                          : 'text-sm text-ink-800 dark:text-ink-100'
                      }
                    >
                      {a.summary}
                    </div>
                  </div>
                  {canUndo && (
                    <button
                      type="button"
                      onClick={() => onUndo(a)}
                      disabled={undo.isPending}
                      className="shrink-0 inline-flex items-center gap-1 rounded-md border border-ink-300 dark:border-ink-700 px-2 py-1 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900 disabled:opacity-50"
                    >
                      <Undo2 size={12} />
                      {t('aiActions.undo')}
                    </button>
                  )}
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
